<script setup lang="ts">
import { computed } from 'vue'
import * as da from '@/strings/da'
import { formatCount, formatDuration, formatMs, formatPercent, formatRate, formatScore } from '@/lib/format'
import type { BrowserMeasurement } from '@/lib/calltest/stats'
import type { PlatformAudio } from '@/lib/calltest/types'

const props = defineProps<{
	platform: PlatformAudio | null
	browser: BrowserMeasurement | null
	/** Peak level of the tone the browser generated, as a sanity check that it really sent one. */
	tonePeak: number
	/** Media time the browser's own counters cover, for the measurement-window row. */
	browserMediaMs: number | null
}>()

interface Row {
	label: string
	platform: string
	browser: string
}

/**
 * The two sides measure the same call from opposite ends, so the columns cross over: the
 * platform's `inbound` is the visitor's microphone reaching us, and the browser's counterpart for
 * that same direction is its *outbound* counters. Getting this backwards would silently swap the
 * two directions in every row.
 */
const rows = computed<Row[]>(() => {
	const platform = props.platform
	const browser = props.browser
	const unreported = da.call.notReported

	// Outbound quality from the platform is only real when the browser sent usable RTCP.
	const platformOutboundQuality = (value: number | null) =>
		platform?.remoteReported ? formatPercent(value) : unreported

	return [
		{
			// The window each side measured, which is what makes the totals below differ.
			label: da.comparison.rows.window,
			platform: formatDuration(platform?.mediaMs ?? null),
			browser: formatDuration(props.browserMediaMs)
		},
		{
			label: `${da.comparison.rows.packets} — ${da.call.yourAudio}`,
			platform: packetsCell(platform?.inbound.packets ?? null, platform?.inbound.packetsPerSecond ?? null),
			browser: packetsCell(browser?.outboundPackets ?? null, browser?.outboundRatePerSecond ?? null)
		},
		{
			label: `${da.comparison.rows.packets} — ${da.call.ourAudio}`,
			platform: packetsCell(platform?.outbound.packets ?? null, platform?.outbound.packetsPerSecond ?? null),
			browser: packetsCell(browser?.inboundPackets ?? null, browser?.inboundRatePerSecond ?? null)
		},
		{
			label: `${da.comparison.rows.loss} — ${da.call.yourAudio}`,
			platform: platform ? formatPercent(lossPercent(platform.inbound.packets, platform.inbound.lost)) : da.call.notMeasured,
			browser: formatPercent(browser?.outboundLossPercent ?? null)
		},
		{
			label: `${da.comparison.rows.loss} — ${da.call.ourAudio}`,
			platform: platform ? platformOutboundQuality(lossPercent(platform.outbound.packets, platform.outbound.lost)) : da.call.notMeasured,
			browser: formatPercent(browser?.inboundLossPercent ?? null)
		},
		{
			label: `${da.comparison.rows.jitter} — ${da.call.yourAudio}`,
			platform: platform ? formatMs(platform.inbound.jitterMs) : da.call.notMeasured,
			browser: formatMs(browser?.outboundJitterMs ?? null)
		},
		{
			label: `${da.comparison.rows.jitter} — ${da.call.ourAudio}`,
			platform: platform
				? (platform.remoteReported ? formatMs(platform.outbound.jitterMs) : unreported)
				: da.call.notMeasured,
			browser: formatMs(browser?.inboundJitterMs ?? null)
		},
		{
			label: da.comparison.rows.rtt,
			platform: platform ? formatMs(platform.rttMs) : da.call.notMeasured,
			browser: formatMs(browser?.rttMs ?? null)
		},
		{
			label: `${da.comparison.rows.mes} — ${da.call.yourAudio}`,
			platform: platform ? formatScore(platform.inbound.mes) : da.call.notMeasured,
			browser: da.call.notMeasured
		},
		{
			label: `${da.comparison.rows.mes} — ${da.call.ourAudio}`,
			platform: platform
				? (platform.remoteReported ? formatScore(platform.outbound.mes) : unreported)
				: da.call.notMeasured,
			browser: da.call.notMeasured
		},
		{
			label: da.comparison.rows.level,
			platform: da.call.notMeasured,
			browser: formatPercent(props.tonePeak * 100)
		}
	]
})

/**
 * A total on its own invites a false comparison, because the two sides may have measured for
 * different lengths of time. The rate beside it is the part that is genuinely comparable.
 */
function packetsCell(total: number | null, rate: number | null): string {
	if (total === null) return da.call.notMeasured
	if (rate === null) return formatCount(total)
	return `${formatCount(total)} (${formatRate(rate)})`
}

function lossPercent(packets: number, lost: number | null): number | null {
	if (lost === null) return null
	const expected = packets + lost
	return expected > 0 ? (lost / expected) * 100 : null
}
</script>

<template>
	<section>
		<h3 class="heading">{{ da.comparison.heading }}</h3>
		<p class="lede">{{ da.comparison.lede }}</p>
		<p class="lede">{{ da.call.toneExplainer }}</p>
		<p class="lede">{{ da.comparison.windowExplainer }}</p>

		<div class="scroller">
			<table class="table">
				<thead>
					<tr>
						<th scope="col">{{ da.comparison.metric }}</th>
						<th scope="col">{{ da.comparison.platform }}</th>
						<th scope="col">{{ da.comparison.browser }}</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="row in rows" :key="row.label">
						<th scope="row">{{ row.label }}</th>
						<td>{{ row.platform }}</td>
						<td>{{ row.browser }}</td>
					</tr>
				</tbody>
			</table>
		</div>
	</section>
</template>

<style scoped>
.heading {
	font-size: 1.0625rem;
	font-weight: 700;
}

.lede {
	margin: 6px 0 14px;
	max-width: 62ch;
	font-size: 0.875rem;
	color: var(--tcx-muted);
}

/* Wide content scrolls inside its own box; the page itself must never scroll sideways. */
.scroller {
	overflow-x: auto;
}

.table {
	width: 100%;
	margin: 0;
	border-collapse: collapse;
	font-size: 0.875rem;
}

thead th {
	padding: 8px 12px 8px 0;
	border-bottom: 1px solid var(--tcx-line);
	text-align: left;
	font-weight: 500;
	color: var(--tcx-muted);
	white-space: nowrap;
}

tbody th {
	padding: 8px 12px 8px 0;
	border-bottom: 1px solid var(--tcx-line);
	text-align: left;
	font-weight: 400;
	color: var(--tcx-text);
}

tbody td {
	padding: 8px 12px 8px 0;
	border-bottom: 1px solid var(--tcx-line);
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
}
</style>
